/* eslint-disable */
import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import AuthContext from "../../contexts/AuthProvider";
import { client } from "../../services/axiosClient";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../hooks/useThemeColors";
import { useResultsFilters } from "../../hooks/useResultsFilters";

import LogisticsSankeyChart from "../../components/Graphs/LogisticsSankeyChart";
import HelpDialog from "../../components/Dialog/HelpDialog";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import Tune from "@material-ui/icons/Tune";
import Fullscreen from "@material-ui/icons/Fullscreen";
import FullscreenExit from "@material-ui/icons/FullscreenExit";
import GetApp from "@material-ui/icons/GetApp";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import HelpOutline from "@material-ui/icons/HelpOutline";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import ActionBar from "components/ActionBar/ActionBar";
import ActionDropdown from "../../components/ActionDropdown/ActionDropdown";
import ResultsFilterBar from "../../components/ResultsFilterBar";
import FullPageLoader from "../../components/Loaders/FullPageLoader";
import { Grid } from "react-loader-spinner";

import styles from "../../assets/jss/material-dashboard-pro-react/views/graphExplorerStyle";

// Default unit options (used until API responds)
const DEFAULT_UNIT_OPTIONS = [
  { meta_name: "volume_pallets", name: "Pallets" },
  { meta_name: "volume_hl", name: "Hectolitros" },
];

// ---------------------------------------------------------------------------
// Grouping: nodes representing < threshold of their layer's total are grouped.
// ---------------------------------------------------------------------------
const GROUP_PCT_THRESHOLD = 0.01; // 1%
const GROUP_MIN_COUNT = 2; // at least N nodes to trigger grouping

/**
 * Build a simple 2-layer Sankey dataset from API nodes+links.
 * Groups small nodes on BOTH sides when groupLabels is provided:
 *   groupLabels[0] -> label for grouped left-side nodes (origins)
 *   groupLabels[1] -> label for grouped right-side nodes (destinations)
 */
function buildSimpleSankey(apiData, selectedMetric, groupLabels) {
  if (
    !apiData ||
    !apiData.nodes ||
    !apiData.links ||
    !selectedMetric ||
    apiData.nodes.length === 0 ||
    apiData.links.length === 0
  )
    return null;

  const origNodes = apiData.nodes;
  const origLinks = apiData.links;

  // Set value from selected metric
  const links = origLinks.map((l) => ({
    ...l,
    value: l.metrics?.[selectedMetric] || 0,
  }));

  // For 2-layer data: left nodes have no incoming links
  const hasIncoming = new Set(links.map((l) => l.target));
  const isLeft = origNodes.map((_, i) => !hasIncoming.has(i));

  // Compute node volume (for grouping)
  const nodeVolume = new Array(origNodes.length).fill(0);
  links.forEach((l) => {
    nodeVolume[l.source] += l.value;
    nodeVolume[l.target] += l.value;
  });

  // Identify small nodes per side (left = layer 0, right = layer 1)
  const smallLeft = [];
  const smallRight = [];

  if (groupLabels) {
    // Left side (origins)
    if (groupLabels[0]) {
      const leftTotal = origNodes.reduce(
        (s, _, i) => s + (isLeft[i] ? nodeVolume[i] : 0),
        0,
      );
      origNodes.forEach((_, i) => {
        if (
          isLeft[i] &&
          leftTotal > 0 &&
          nodeVolume[i] / leftTotal < GROUP_PCT_THRESHOLD &&
          nodeVolume[i] > 0
        ) {
          smallLeft.push(i);
        }
      });
      if (smallLeft.length < GROUP_MIN_COUNT) smallLeft.length = 0;
    }
    // Right side (destinations)
    if (groupLabels[1]) {
      const rightTotal = origNodes.reduce(
        (s, _, i) => s + (!isLeft[i] ? nodeVolume[i] : 0),
        0,
      );
      origNodes.forEach((_, i) => {
        if (
          !isLeft[i] &&
          rightTotal > 0 &&
          nodeVolume[i] / rightTotal < GROUP_PCT_THRESHOLD &&
          nodeVolume[i] > 0
        ) {
          smallRight.push(i);
        }
      });
      if (smallRight.length < GROUP_MIN_COUNT) smallRight.length = 0;
    }
  }

  const allSmall = new Set([...smallLeft, ...smallRight]);

  // Build new nodes
  const newNodes = [];
  const oldToNew = {};

  origNodes.forEach((n, i) => {
    if (allSmall.has(i)) return;
    oldToNew[i] = newNodes.length;
    newNodes.push({ ...n });
  });

  // Create group node for left side
  if (smallLeft.length > 0) {
    const memberNames = smallLeft.map((i) => origNodes[i]?.name || `#${i}`);
    const label = `+ ${smallLeft.length} ${(groupLabels[0] || "otros").toLowerCase()}`;
    const gIdx = newNodes.length;
    newNodes.push({ name: label, members: memberNames, _grouped: true });
    smallLeft.forEach((i) => {
      oldToNew[i] = gIdx;
    });
  }

  // Create group node for right side
  if (smallRight.length > 0) {
    const memberNames = smallRight.map((i) => origNodes[i]?.name || `#${i}`);
    const label = `+ ${smallRight.length} ${(groupLabels[1] || "otros").toLowerCase()}`;
    const gIdx = newNodes.length;
    newNodes.push({ name: label, members: memberNames, _grouped: true });
    smallRight.forEach((i) => {
      oldToNew[i] = gIdx;
    });
  }

  // Rebuild links with new indices, merging duplicates
  // non_finished links are kept separate from finished (different visual style)
  const linkMap = {};
  links.forEach((l) => {
    const ns = oldToNew[l.source];
    const nt = oldToNew[l.target];
    if (ns === undefined || nt === undefined || ns === nt) return;
    const ltFlag = l.link_type ? `:${l.link_type}` : "";
    const key = `${ns}->${nt}${ltFlag}`;
    if (!linkMap[key]) {
      linkMap[key] = {
        source: ns,
        target: nt,
        value: 0,
        metrics: {},
        _members: [],
      };
      if (l.link_type) linkMap[key].link_type = l.link_type;
    }
    linkMap[key].value += l.value;
    Object.entries(l.metrics || {}).forEach(([k, v]) => {
      linkMap[key].metrics[k] = (linkMap[key].metrics[k] || 0) + (v || 0);
    });
    if (oldToNew[l.source] !== l.source || oldToNew[l.target] !== l.target) {
      const srcName = origNodes[l.source]?.name;
      const tgtName = origNodes[l.target]?.name;
      if (srcName && !linkMap[key]._members.includes(srcName))
        linkMap[key]._members.push(srcName);
      if (tgtName && !linkMap[key]._members.includes(tgtName))
        linkMap[key]._members.push(tgtName);
    }
  });

  const newLinks = Object.values(linkMap).filter((l) => l.value >= 0.5);
  newLinks.forEach((l) => {
    if (l._members.length === 0) delete l._members;
    // Store metrics and node names for link tooltip (SankeyLink renders its own tooltip)
    l._m = { ...(l.metrics || {}) };
    l.sourceName = newNodes[l.source]?.name || "";
    l.targetName = newNodes[l.target]?.name || "";
  });

  if (newNodes.length === 0 || newLinks.length === 0) return null;

  // Remove orphaned nodes (no links after filtering) and re-index
  const usedNodes = new Set();
  newLinks.forEach((l) => { usedNodes.add(l.source); usedNodes.add(l.target); });
  const finalNodes = [];
  const reindex = {};
  newNodes.forEach((n, i) => {
    if (!usedNodes.has(i)) return;
    reindex[i] = finalNodes.length;
    finalNodes.push(n);
  });
  const finalLinks = newLinks.map((l) => ({
    ...l,
    source: reindex[l.source],
    target: reindex[l.target],
    sourceName: finalNodes[reindex[l.source]]?.name || "",
    targetName: finalNodes[reindex[l.target]]?.name || "",
  }));

  // Aggregate metrics per node (sum of all touching links) for tooltip display
  finalNodes.forEach((n) => { n.metrics = {}; });
  finalLinks.forEach((l) => {
    Object.entries(l.metrics || {}).forEach(([k, v]) => {
      finalNodes[l.source].metrics[k] =
        (finalNodes[l.source].metrics[k] || 0) + (v || 0);
      finalNodes[l.target].metrics[k] =
        (finalNodes[l.target].metrics[k] || 0) + (v || 0);
    });
  });

  if (finalNodes.length === 0 || finalLinks.length === 0) return null;
  return { nodes: finalNodes, links: finalLinks };
}

// ==========================================================================
// Component
// ==========================================================================
const useStyles = makeStyles(styles);

export default function LogisticsFlow() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor } = useThemeColors();

  // -- Core state --
  const [selectedMetric, setSelectedMetric] = useState(null); // set from API response
  const [focusMode, setFocusMode] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // -- Sankey API state --
  const [sankeyResponse, setSankeyResponse] = useState(null);
  const [isSankeyLoading, setIsSankeyLoading] = useState(false);
  const [sankeyError, setSankeyError] = useState(null);
  const transfersLayers = sankeyResponse?.transfers_layers || [];
  const transfersGroupLabels = sankeyResponse?.transfers_group_labels || [];
  const transfersLinkTypes = sankeyResponse?.transfers_link_types || [];
  // Build color map from backend link type config: { key: color }
  const transfersLinkTypeColors = useMemo(
    () => transfersLinkTypes.reduce((m, t) => { m[t.key] = t.color; return m; }, {}),
    [transfersLinkTypes],
  );
  const deliveryLayers = sankeyResponse?.delivery_layers || [];
  const deliveryGroupLabels = sankeyResponse?.delivery_group_labels || [];
  const unitOptions = sankeyResponse?.unit_options || DEFAULT_UNIT_OPTIONS;

  // -- Filtros: gerenciados pelo hook unificado --
  const {
    filtersList,
    filtersNames,
    filtersItems,
    filtersGroups,
    metaLoaded,
    commonFilters,
    commonFiltersMode,
    commonFiltersIndexes,
    commonFiltersSequence,
    getFilterItems,
    filtersListTranslated,
    filtersActiveList,
    filtersSequenceNums,
    getFiltersInSequence,
    filterMenuClickHandler,
    removeFilter,
    filterDialogProps,
    dimFilterDialogProps,
    availablePeriods,
    selectedPeriodKeys,
    activePreset,
    handlePeriodApply,
    handleResetFilters,
    dimSections,
    persist,
    setPersist,
  } = useResultsFilters();

  // -- ActionBar --
  const handleUnitSelection = (idx) => {
    if (idx < unitOptions.length) setSelectedMetric(unitOptions[idx].meta_name);
  };
  const currentUnitLabel =
    unitOptions.find((u) => u.meta_name === selectedMetric)?.name || "Pallets";

  const actionBarContent = (
    <>
      {/* Unit selector */}
      <ActionDropdown
        button={
          <GridItem className={classes.actionWrapperOuter}>
            <div className={classes.actionWrapper}>
              <div className={classes.actionIcon}>
                <Tune />
              </div>
              <h6 className={classes.actionText}>{currentUnitLabel}</h6>
            </div>
          </GridItem>
        }
        dropdownList={unitOptions.map((u) => u.name)}
        onClick={handleUnitSelection}
      />
      {/* Focus */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={classes.actionWrapper}
          onClick={() => setFocusMode((f) => !f)}
          style={{ cursor: "pointer" }}
        >
          <div className={classes.actionIcon}>
            {focusMode ? <FullscreenExit /> : <Fullscreen />}
          </div>
          <h6 className={classes.actionText}>
            {t("logistics_flow.focus_mode")}
          </h6>
        </div>
      </GridItem>
      {/* Disabled placeholders */}
      <GridItem className={classes.actionWrapperOuter}>
        <div className={cx(classes.actionWrapper, classes.actionDisabled)}>
          <div className={classes.actionIcon}>
            <GetApp />
          </div>
          <h6 className={classes.actionText}>
            {t("results.download")}
          </h6>
        </div>
      </GridItem>
      <GridItem className={classes.actionWrapperOuter}>
        <div className={cx(classes.actionWrapper, classes.actionDisabled)}>
          <div className={classes.actionIcon}>
            <PhotoCamera />
          </div>
          <h6 className={classes.actionText}>
            {t("results.take_photo")}
          </h6>
        </div>
      </GridItem>
      {/* Help */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={classes.actionWrapper}
          onClick={() => setHelpDialogOpen(true)}
          style={{ cursor: "pointer" }}
        >
          <div className={classes.actionIcon}>
            <HelpOutline />
          </div>
          <h6 className={classes.actionText}>{t("results.help")}</h6>
        </div>
      </GridItem>
    </>
  );

  // -- Fetch Sankey data from API (with abort for race condition) --
  const abortRef = useRef(null);
  const fetchSankey = useCallback(() => {
    // Abort any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSankeyLoading(true);
    setSankeyError(null);

    // Build filter payload
    const apiFilters = {};
    Object.keys(commonFilters).forEach((f) => {
      if (f === "period") return; // period sent as year/month params below
      if (
        commonFilters[f] &&
        commonFilters[f].length > 0 &&
        commonFiltersMode[f]
      ) {
        apiFilters[f] = commonFilters[f];
      }
    });

    // Extract period params from the period filter
    const periodParams = {};
    const pf = commonFilters["period"];
    const pfMode = commonFiltersMode["period"];
    if (pfMode === "items" && pf && pf.length > 0) {
      // Items mode: each item is [year, month]
      const first = pf.reduce(
        (min, p) => (p[0] * 100 + p[1] < min[0] * 100 + min[1] ? p : min),
        pf[0],
      );
      const last = pf.reduce(
        (max, p) => (p[0] * 100 + p[1] > max[0] * 100 + max[1] ? p : max),
        pf[0],
      );
      periodParams.year_start = first[0];
      periodParams.month_start = first[1];
      periodParams.year_end = last[0];
      periodParams.month_end = last[1];
    } else if (pfMode === "group" && pf && pf.length > 0) {
      // Group mode: resolve group name to period range using available items
      // Mirrors PeriodFilters.get_periods_list logic from backend
      const allItems = filtersItems.current["period"] || [];
      if (allItems.length > 0) {
        const group = pf[0]; // e.g., "l12m", "l6m", "l3m", "lm", "ytd"
        let selectedItems = allItems; // default: all available

        const match = group.match(/^l(\d+)m$/);
        if (match) {
          selectedItems = allItems.slice(-parseInt(match[1]));
        } else if (group === "lm") {
          selectedItems = allItems.slice(-1);
        } else if (group === "ytd") {
          const latestYear = allItems[allItems.length - 1][0];
          selectedItems = allItems.filter((p) => p[0] === latestYear);
        } else if (group === "ytd_ly") {
          const latestYear = allItems[allItems.length - 1][0];
          selectedItems = allItems.filter((p) => p[0] === latestYear - 1);
        }

        if (selectedItems.length > 0) {
          periodParams.year_start = selectedItems[0][0];
          periodParams.month_start = selectedItems[0][1];
          periodParams.year_end = selectedItems[selectedItems.length - 1][0];
          periodParams.month_end = selectedItems[selectedItems.length - 1][1];
        }
      }
    }

    client
      .post("api/logistics_sankey/", { filters: apiFilters, ...periodParams }, { signal: controller.signal })
      .then((res) => {
        if (res.data.success) {
          setSankeyResponse(res.data);
          // Set metric from API (first load or if current metric not available)
          if (res.data.default_metric) {
            const available = (res.data.unit_options || []).map(
              (u) => u.meta_name,
            );
            if (!selectedMetric || !available.includes(selectedMetric)) {
              setSelectedMetric(res.data.default_metric);
            }
          }
        } else {
          setSankeyError(res.data.error_message || t("logistics_flow.no_data"));
          setSankeyResponse(null);
        }
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return; // aborted
        console.error("Sankey fetch failed:", err);
        setSankeyError(t("logistics_flow.error"));
        setSankeyResponse(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsSankeyLoading(false);
      });
  }, [commonFilters, commonFiltersMode]);

  // Fetch on mount and when filters change
  useEffect(() => {
    if (metaLoaded) fetchSankey();
  }, [metaLoaded, fetchSankey]);

  // -- Chart data: two separate Sankeys --
  const transfersData = useMemo(
    () =>
      buildSimpleSankey(
        sankeyResponse?.transfers,
        selectedMetric,
        transfersGroupLabels,
      ),
    [sankeyResponse, selectedMetric, transfersGroupLabels],
  );
  const deliveryData = useMemo(
    () =>
      buildSimpleSankey(
        sankeyResponse?.delivery,
        selectedMetric,
        deliveryGroupLabels,
      ),
    [sankeyResponse, selectedMetric, deliveryGroupLabels],
  );

  const sectionHeaderStyle = {
    color: "rgb(120, 120, 120)",
    backgroundColor: "rgba(238, 238, 238, 0.267)",
    fontSize: "14px",
    fontWeight: "400",
    padding: "2px 8px",
    borderRadius: "6px 6px 0 0",
    textTransform: "uppercase",
    textAlign: "center",
    borderBottom: "1px solid " + clientColor,
  };

  const sankeyCard = (data, layers, title, extra) => (
    <Card style={{ margin: 0, height: "100%", borderRadius: 6 }}>
      <div style={sectionHeaderStyle}>{title}</div>
      <CardBody style={{ padding: "5px 0", flex: 1, overflow: "hidden" }}>
        <div style={{ width: "100%", height: "100%" }}>
          <LogisticsSankeyChart
            data={data}
            layers={layers}
            unitOptions={unitOptions}
            clientColor={clientColor}
            groupedLabel={t("logistics_flow.grouped")}
            {...extra}
          />
        </div>
      </CardBody>
    </Card>
  );

  const transfersLegend = transfersLinkTypes.length > 0 ? (
    <div style={{ display: "flex", gap: 12, justifyContent: "center", padding: "2px 0", fontSize: 10, color: "#888" }}>
      {transfersLinkTypes.map((lt) => (
        <span key={lt.key} style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: lt.color, display: "inline-block" }} />
          {lt.label}
        </span>
      ))}
    </div>
  ) : null;

  const hasTransfers = transfersData && transfersData.nodes?.length > 0;
  const hasDelivery = deliveryData && deliveryData.nodes?.length > 0;

  const viewContent = (
    <div style={{ display: "flex", width: "100%", height: "100%", gap: 8 }}>
      {hasTransfers && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card style={{ margin: 0, height: "100%", borderRadius: 6 }}>
            <div style={sectionHeaderStyle}>
              {t("logistics_flow.transfers")}
            </div>
            {transfersLegend}
            <CardBody style={{ padding: "5px 0", flex: 1, overflow: "hidden" }}>
              <div style={{ width: "100%", height: "100%" }}>
                <LogisticsSankeyChart
                  data={transfersData}
                  layers={transfersLayers}
                  unitOptions={unitOptions}
                  clientColor={clientColor}
                  selectedMetric={selectedMetric}
                  linkTypeColors={transfersLinkTypeColors}
                />
              </div>
            </CardBody>
          </Card>
        </div>
      )}
      {hasDelivery && (
        <div style={{ flex: 1, minWidth: 0 }}>
          {sankeyCard(
            deliveryData,
            deliveryLayers,
            t("logistics_flow.delivery"),
          )}
        </div>
      )}
    </div>
  );

  // -- Loading --
  if (!metaLoaded)
    return (
      <div>
        <FullPageLoader isVisible={true} color={clientColor} />
      </div>
    );

  // -- Render --
  return (
    <div>
      <ActionBar actions={actionBarContent} spinnerIn={isSankeyLoading} />
      <GridContainer style={{ marginTop: "-28px" }}>
        {/* Filtros */}
        <ResultsFilterBar
          filtersList={filtersList}
          filtersNames={filtersNames}
          filtersGroups={filtersGroups}
          commonFilters={commonFilters}
          commonFiltersMode={commonFiltersMode}
          commonFiltersSequence={commonFiltersSequence}
          filterMenuClickHandler={filterMenuClickHandler}
          removeFilter={removeFilter}
          dimFilterDialogProps={dimFilterDialogProps}
          clientColor={clientColor}
          sections={dimSections}
          showPeriodExtras
          periodExtrasProps={{
            availablePeriods,
            selectedPeriodKeys,
            activePreset,
            onPeriodApply: handlePeriodApply,
            onReset: handleResetFilters,
            persist: persist,
            onPersistChange: setPersist,
          }}
        />
        {/* Main content */}
        <GridItem xs={12}>
          <div
            style={{
              height: "calc(100vh - 170px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isSankeyLoading ? (
              <Grid color={clientColor} height={40} width={40} />
            ) : sankeyError ? (
              <div
                style={{ textAlign: "center", color: "#999", fontSize: 13 }}
              >
                {sankeyError}
              </div>
            ) : hasTransfers || hasDelivery ? (
              viewContent
            ) : (
              <div
                style={{ textAlign: "center", color: "#999", fontSize: 13 }}
              >
                {t("logistics_flow.no_data")}
              </div>
            )}
          </div>
        </GridItem>
      </GridContainer>

      {/* Focus mode overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#eeeeee",
          zIndex: 1200,
          opacity: focusMode ? 1 : 0,
          visibility: focusMode ? "visible" : "hidden",
          transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
          padding: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <ActionDropdown
              button={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                    background: "#fff",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#444",
                  }}
                >
                  <Tune style={{ fontSize: 16 }} />
                  <span>{currentUnitLabel}</span>
                </div>
              }
              dropdownList={unitOptions.map((u) => u.name)}
              onClick={handleUnitSelection}
            />
          </div>
          <button
            onClick={() => setFocusMode(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
              background: "#fff",
              fontSize: 12,
              color: "#666",
            }}
          >
            <FullscreenExit style={{ fontSize: 16 }} />
            <span>{t("logistics_flow.focus_mode")}</span>
          </button>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          {focusMode && viewContent}
        </div>
      </div>

      {/* Help dialog */}
      <HelpDialog
        open={helpDialogOpen}
        setOpen={setHelpDialogOpen}
        title={t("help.logistics_flow.title")}
        content={[
          {
            icon: "Purpose",
            title: t("help.logistics_flow.purpose.title"),
            items: [
              {
                title: t("help.logistics_flow.purpose.objective.title"),
                description: t("help.logistics_flow.purpose.objective.description"),
              },
            ],
          },
          {
            icon: "BasicFunctionality",
            title: t("help.logistics_flow.basic_functionality.title"),
            items: [
              {
                title: t("help.logistics_flow.basic_functionality.transfers_chart.title"),
                description: t("help.logistics_flow.basic_functionality.transfers_chart.description"),
              },
              {
                title: t("help.logistics_flow.basic_functionality.delivery_chart.title"),
                description: t("help.logistics_flow.basic_functionality.delivery_chart.description"),
              },
              {
                title: t("help.logistics_flow.basic_functionality.filters.title"),
                description: t("help.logistics_flow.basic_functionality.filters.description"),
              },
              {
                title: t("help.logistics_flow.basic_functionality.unit_selector.title"),
                description: t("help.logistics_flow.basic_functionality.unit_selector.description"),
              },
            ],
          },
          {
            icon: "Analysis",
            title: t("help.logistics_flow.analysis.title"),
            items: [
              {
                title: t("help.logistics_flow.analysis.reading_charts.title"),
                description: t("help.logistics_flow.analysis.reading_charts.description"),
              },
              {
                title: t("help.logistics_flow.analysis.grouped_nodes.title"),
                description: t("help.logistics_flow.analysis.grouped_nodes.description"),
              },
            ],
          },
          {
            icon: "OtherFeatures",
            title: t("help.logistics_flow.other_features.title"),
            items: [
              {
                title: t("help.logistics_flow.other_features.focus_mode.title"),
                description: t("help.logistics_flow.other_features.focus_mode.description"),
              },
            ],
          },
        ]}
      />

    </div>
  );
}
